import errorCodes from 'client/errorCodes';
import {query} from '@albert-io/json-api-framework/request/builder';
import {genericMandarkRequest} from 'resources/mandark.resource';

import {createBasicValidatons, createRecordValidator} from './useCreateAccounts.validators';
import {makeManagedUsersPayload} from './useCreateAccounts.queries';
// TODO: can remove exports when this file is deleted

const endpoint = query()
  .mandarkEndpoint(['managed_users_v1'])
  .customHeader({'Content-Type': 'application/vnd.api+json; ext=bulk'})
  .done();

function addManagedUsersQuery(payload) {
  return genericMandarkRequest('post', endpoint, payload, {raw: true});
}

const indexDuplicateEntries = (items, key) => {
  return items
    .groupBy((item) => item.get(key))
    .toList()
    .filter((group) => group.size > 1)
    .map((group) => group.shift()) // Remove the first appearance of the duplicate from our index, since it's the first
    .flatten(1);
};

const createOldDuplicateValidators = (records) => {
  const duplicateEmails = indexDuplicateEntries(records, 'email');
  const duplicateUsernames = indexDuplicateEntries(records, 'username');
  let validators = [];

  if (!duplicateEmails.isEmpty()) {
    // duplicate emails
    validators = [
      ...validators,
      [
        (item) => !duplicateEmails.includes(item),
        'duplicateEmail',
        'This email address appears more than once. Only one instance of each duplicate will be used.'
      ]
    ];
  }
  if (!duplicateUsernames.isEmpty()) {
    // duplicate usernames
    validators = [
      ...validators,
      [
        (item) => !duplicateUsernames.includes(item),
        'duplicateUsername',
        'This username appears more than once. Only the first instance of each duplicate will be used.'
      ]
    ];
  }

  return validators;
};

const createUniqueConstraintValidators = (error) => {
  const validators = [];
  const {email: emails = [], username: usernames = []} = error.response.body.errors[0].detail;

  emails.forEach((email) => {
    validators.push([
      (item) => item.get('email') !== email,
      'nonUniqueEmail',
      'An account with this email address already exists'
    ]);
  });

  usernames.forEach((username) => {
    validators.push([
      (item) => item.get('username') !== username,
      'nonUniqueUsername',
      'An account with this username already exists'
    ]);
  });

  return validators;
};

const hasOldErrorMethod = (error) => {
  return (
    error?.response?.body?.errors[0].code ===
    errorCodes.BAD_REQUEST.VALIDATION_ERROR.UNIQUE_CONSTRAINT_VIOLATED
  ); // This error only exists on old method
};

export const getOldOfflineValidations = (records, emailDomains) => {
  const basicValidations = createBasicValidatons(emailDomains);
  const duplicateValidations = createOldDuplicateValidators(records);
  const offlineValidations = [...basicValidations, ...duplicateValidations];
  const validator = createRecordValidator(offlineValidations);
  const [entries, validEntries, invalidEntries] = validator(records);

  return {entries, validEntries, invalidEntries};
};

export const getOldServerValidations = (records, emailDomains, error) => {
  const basicValidations = createBasicValidatons(emailDomains);
  const duplicateValidations = createOldDuplicateValidators(records);

  let validations = [...basicValidations];

  if (hasOldErrorMethod(error)) {
    const uniqueValidations = createUniqueConstraintValidators(error);
    validations = [...validations, ...uniqueValidations];
  }

  validations = [...validations, ...duplicateValidations];

  const validator = createRecordValidator(validations);

  const [entries, validEntries, invalidEntries] = validator(records);

  return {entries, validEntries, invalidEntries};
};

const legacyBulkCreateSchoolAccounts = async (
  records,
  emailDomains,
  coppaConsentGrantorEmail,
  school
) => {
  const {validEntries, invalidEntries, entries} = getOldOfflineValidations(records, emailDomains);
  try {
    const payload = makeManagedUsersPayload(coppaConsentGrantorEmail, school, validEntries);
    const res = await addManagedUsersQuery(payload);
    return {response: res, invalidEntries, validEntries, error: null, entries};
  } catch (err) {
    const validatedResults = getOldServerValidations(entries, emailDomains, err);
    const oldValidationPayload = makeManagedUsersPayload(
      coppaConsentGrantorEmail,
      school,
      validatedResults.validEntries
    );

    try {
      if (validatedResults.validEntries.isEmpty()) {
        throw new Error('Bulk create failed with all unique constraint violations');
      }
      const res = await addManagedUsersQuery(oldValidationPayload);
      return {
        response: res,
        ...validatedResults,
        error: null
      };
    } catch (lastError) {
      return {response: null, error: lastError, ...validatedResults};
    }
  }
};

export default legacyBulkCreateSchoolAccounts;
